import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { Button } from '@/components/ui/Button';
import { ProgressIndicator } from '../CampaignCreation/ProgressIndicator';
import { AssetGroupBasicsStep } from './steps/AssetGroupBasicsStep';
import { AssetGroupTextAssetsStep } from './steps/AssetGroupTextAssetsStep';
import { AssetGroupImagesStep } from './steps/AssetGroupImagesStep';
import { AssetGroupReviewStep } from './steps/AssetGroupReviewStep';
import { campaignsApi } from '@/services/api/campaigns';
import { useMaterializePendingTargeted } from '@/hooks/useMaterializePendingTargeted';
import { batchDeleteImages } from '@/services/api/media';
import { debug } from '@/utils/debug';
import { isUrlGloballyAuthorized } from '@/utils/urlUtils';
import { useAuthStore } from '@/store/authStore';
import type { Campaign } from '@/types/campaign';
import type { AppConfiguration } from '@/types/api';
import {
  type AssetGroupFormData,
  type CreateAssetGroupRequest,
  DEFAULT_ASSET_GROUP_FORM_DATA,
  ASSET_GROUP_LIMITS,
} from '@/types/assetGroup';

interface CreateAssetGroupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  campaignId: string;
  campaignName: string;
}

interface FormErrors {
  name?: string;
  finalUrl?: string;
  headlines?: string;
  longHeadline?: string;
  descriptions?: string;
  businessName?: string;
  landscapeImages?: string;
  squareImages?: string;
  logoImages?: string;
}

const WIZARD_STEPS = [
  { label: 'Basics', description: '' },
  { label: 'Text Assets', description: '' },
  { label: 'Images', description: '' },
  { label: 'Review', description: '' },
];

export const CreateAssetGroupWizard: React.FC<CreateAssetGroupWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaignId,
  campaignName,
}) => {
  const queryClient = useQueryClient();
  const { memberUuid } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const materializePending = useMaterializePendingTargeted();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formData, setFormData] = useState<AssetGroupFormData>(DEFAULT_ASSET_GROUP_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});

  // Reset form when wizard opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setErrors({});
      setSubmitError(null);
      setFormData(DEFAULT_ASSET_GROUP_FORM_DATA);
    }
  }, [isOpen]);

  // Validation
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      // Basics step
      if (!formData.name.trim()) {
        newErrors.name = 'Asset group name is required';
      } else if (formData.name.length > ASSET_GROUP_LIMITS.name.max) {
        newErrors.name = `Name must be ${ASSET_GROUP_LIMITS.name.max} characters or less`;
      }

      if (!formData.finalUrl.trim()) {
        newErrors.finalUrl = 'Final URL is required';
      } else if (!formData.finalUrl.match(/^https?:\/\/.+/)) {
        newErrors.finalUrl = 'URL must start with http:// or https://';
      }
    }

    if (step === 2) {
      // Text assets step
      const filledHeadlines = formData.headlines.filter((h) => h.trim().length > 0);
      if (filledHeadlines.length < ASSET_GROUP_LIMITS.headline.min) {
        newErrors.headlines = `At least ${ASSET_GROUP_LIMITS.headline.min} headlines are required`;
      } else if (filledHeadlines.some((h) => h.length > ASSET_GROUP_LIMITS.headline.charLimit)) {
        newErrors.headlines = `Headlines must be ${ASSET_GROUP_LIMITS.headline.charLimit} characters or less`;
      }

      if (!formData.longHeadline.trim()) {
        newErrors.longHeadline = 'Long headline is required';
      } else if (formData.longHeadline.length > ASSET_GROUP_LIMITS.longHeadline.charLimit) {
        newErrors.longHeadline = `Long headline must be ${ASSET_GROUP_LIMITS.longHeadline.charLimit} characters or less`;
      }

      const filledDescriptions = formData.descriptions.filter((d) => d.trim().length > 0);
      if (filledDescriptions.length < ASSET_GROUP_LIMITS.description.min) {
        newErrors.descriptions = `At least ${ASSET_GROUP_LIMITS.description.min} descriptions are required`;
      } else if (filledDescriptions.some((d) => d.length > ASSET_GROUP_LIMITS.description.charLimit)) {
        newErrors.descriptions = `Descriptions must be ${ASSET_GROUP_LIMITS.description.charLimit} characters or less`;
      }

      if (!formData.businessName.trim()) {
        newErrors.businessName = 'Business name is required';
      } else if (formData.businessName.length > ASSET_GROUP_LIMITS.businessName.charLimit) {
        newErrors.businessName = `Business name must be ${ASSET_GROUP_LIMITS.businessName.charLimit} characters or less`;
      }
    }

    if (step === 3) {
      // Images step
      if (formData.landscapePending.length < ASSET_GROUP_LIMITS.landscapeImage.min) {
        newErrors.landscapeImages = `At least ${ASSET_GROUP_LIMITS.landscapeImage.min} landscape image is required`;
      }

      if (formData.squarePending.length < ASSET_GROUP_LIMITS.squareImage.min) {
        newErrors.squareImages = `At least ${ASSET_GROUP_LIMITS.squareImage.min} square image is required`;
      }

      if (formData.logoPending.length < ASSET_GROUP_LIMITS.logoImage.min) {
        newErrors.logoImages = `At least ${ASSET_GROUP_LIMITS.logoImage.min} logo image is required`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Materialize all pending crops just before submit. Each call writes
      // one Targeted MediaImage to GCS+DB. Done now (not at picker confirm)
      // so abandoned wizard runs leave zero orphans. `materializePending`
      // handles partial-failure cleanup within a single bucket; the
      // post-materialization try/catch below additionally cleans up across
      // all 3 buckets if `createAssetGroup` fails.
      const [landscapeMaterialized, squareMaterialized, logoMaterialized] =
        await Promise.all([
          materializePending(formData.landscapePending),
          materializePending(formData.squarePending),
          materializePending(formData.logoPending),
        ]);
      const allMaterializedIds = [
        ...landscapeMaterialized, ...squareMaterialized, ...logoMaterialized,
      ].map((t) => t.id);
      const cleanupMaterialized = async () => {
        if (allMaterializedIds.length === 0 || !memberUuid) return;
        try {
          await batchDeleteImages({
            member_uuid: memberUuid,
            image_ids: allMaterializedIds,
          });
        } catch (cleanupErr) {
          debug.error('CreateAssetGroupWizard: cleanup of materialized Targeted ids failed', cleanupErr);
        }
      };

      try {
      // Build API request
      const request: CreateAssetGroupRequest = {
        name: formData.name.trim(),
        status: formData.status,
        final_url: formData.finalUrl.trim(),
        headlines: formData.headlines.filter((h) => h.trim().length > 0),
        long_headline: formData.longHeadline.trim(),
        descriptions: formData.descriptions.filter((d) => d.trim().length > 0),
        business_name: formData.businessName.trim(),
        landscape_image_ids: landscapeMaterialized.map((t) => t.id),
        square_image_ids: squareMaterialized.map((t) => t.id),
        logo_image_ids: logoMaterialized.map((t) => t.id),
      };

      debug.log('CreateAssetGroupWizard: Submitting asset group creation request', { request });

      const response = await campaignsApi.createAssetGroup(campaignId, request);

      debug.log('CreateAssetGroupWizard: Asset group created successfully', response);

      // Build optimistic text assets
      const optimisticAssets = [
        ...formData.headlines.filter((h) => h.trim()).map((text, i) => ({
          asset_id: `optimistic_headline_${Date.now()}_${i}`,
          asset_type: 'headline' as const,
          type: 'headline' as const,
          text,
        })),
        {
          asset_id: `optimistic_long_headline_${Date.now()}`,
          asset_type: 'long_headline' as const,
          type: 'long_headline' as const,
          text: formData.longHeadline,
        },
        ...formData.descriptions.filter((d) => d.trim()).map((text, i) => ({
          asset_id: `optimistic_desc_${Date.now()}_${i}`,
          asset_type: 'description' as const,
          type: 'description' as const,
          text,
        })),
        {
          asset_id: `optimistic_business_${Date.now()}`,
          asset_type: 'business_name' as const,
          type: 'business_name' as const,
          text: formData.businessName,
        },
      ];

      // Check if URL is globally authorized in existing config
      const existingConfig = memberUuid ? queryClient.getQueryData<AppConfiguration>(['app-config', memberUuid]) : null;
      const urlIsAuthorized = isUrlGloballyAuthorized(formData.finalUrl, existingConfig);
      debug.log('CreateAssetGroupWizard: URL authorization check', { url: formData.finalUrl, urlIsAuthorized });

      // Build optimistic asset group object
      const newAssetGroup = {
        asset_group_id: response.asset_group_id,
        asset_group_name: response.asset_group_name,
        asset_group_status: formData.status,
        status: formData.status,
        final_urls: [{ url: formData.finalUrl, flouri_scraping_enabled: urlIsAuthorized }],
        final_url_list: [formData.finalUrl],
        asset_list: optimisticAssets,
      };

      // Optimistically update the campaign-from-cache query
      queryClient.setQueryData<Campaign>(['campaign-from-cache', campaignId], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          asset_group_list: [...(oldData.asset_group_list || []), newAssetGroup],
        } as Campaign;
      });

      // Also update app-config cache since useCampaignFromCache derives from it
      if (memberUuid) {
        queryClient.setQueryData<AppConfiguration>(['app-config', memberUuid], (oldConfig) => {
          if (!oldConfig?.configuration?.campaign_list) return oldConfig;
          return {
            ...oldConfig,
            configuration: {
              ...oldConfig.configuration,
              campaign_list: oldConfig.configuration.campaign_list.map((camp) => {
                if (String(camp.campaign_id) === String(campaignId)) {
                  return {
                    ...camp,
                    asset_group_list: [...(camp.asset_group_list || []), newAssetGroup],
                  };
                }
                return camp;
              }),
            },
          };
        });
      }

      debug.log('CreateAssetGroupWizard: Optimistically updated campaign and app-config caches');

      // DON'T invalidate app-config - that would trigger a refetch from Google Ads which
      // may not have propagated the new asset group yet, overwriting our optimistic update.
      // Instead, just invalidate campaign-from-cache to trigger a re-derive from the
      // already-updated app-config cache.
      queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });

      await onSuccess();
      onClose();
      } catch (submitErr) {
        await cleanupMaterialized();
        throw submitErr;
      }
    } catch (error: unknown) {
      debug.error('CreateAssetGroupWizard: Failed to create asset group', error);

      let errorMessage = 'Failed to create asset group. Please try again.';

      const apiError = error as {
        response?: { data?: { detail?: string; message?: string; error?: string } };
        message?: string;
      };
      if (apiError.response?.data) {
        const data = apiError.response.data;
        errorMessage = data.detail || data.message || data.error || errorMessage;

        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Create New Asset Group</h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a new asset group to <span className="font-medium">{campaignName}</span>
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={WIZARD_STEPS.length}
            steps={WIZARD_STEPS}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Step Content */}
        <div
          ref={scrollBodyRef}
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {currentStep === 1 && (
            <AssetGroupBasicsStep
              name={formData.name}
              status={formData.status}
              finalUrl={formData.finalUrl}
              onChangeName={(name) => setFormData((prev) => ({ ...prev, name }))}
              onChangeStatus={(status) => setFormData((prev) => ({ ...prev, status }))}
              onChangeFinalUrl={(finalUrl) => setFormData((prev) => ({ ...prev, finalUrl }))}
              errors={{ name: errors.name, finalUrl: errors.finalUrl }}
            />
          )}

          {currentStep === 2 && (
            <AssetGroupTextAssetsStep
              headlines={formData.headlines}
              longHeadline={formData.longHeadline}
              descriptions={formData.descriptions}
              businessName={formData.businessName}
              onChangeHeadlines={(headlines) => setFormData((prev) => ({ ...prev, headlines }))}
              onChangeLongHeadline={(longHeadline) => setFormData((prev) => ({ ...prev, longHeadline }))}
              onChangeDescriptions={(descriptions) => setFormData((prev) => ({ ...prev, descriptions }))}
              onChangeBusinessName={(businessName) => setFormData((prev) => ({ ...prev, businessName }))}
              errors={{
                headlines: errors.headlines,
                longHeadline: errors.longHeadline,
                descriptions: errors.descriptions,
                businessName: errors.businessName,
              }}
            />
          )}

          {currentStep === 3 && (
            <AssetGroupImagesStep
              landscapePending={formData.landscapePending}
              squarePending={formData.squarePending}
              logoPending={formData.logoPending}
              onChangeLandscapePending={(landscapePending) =>
                setFormData((prev) => ({ ...prev, landscapePending }))
              }
              onChangeSquarePending={(squarePending) =>
                setFormData((prev) => ({ ...prev, squarePending }))
              }
              onChangeLogoPending={(logoPending) =>
                setFormData((prev) => ({ ...prev, logoPending }))
              }
              errors={{
                landscapeImages: errors.landscapeImages,
                squareImages: errors.squareImages,
                logoImages: errors.logoImages,
              }}
            />
          )}

          {currentStep === 4 && (
            <AssetGroupReviewStep
              formData={formData}
              campaignName={campaignName}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Step {currentStep} of {WIZARD_STEPS.length}
            </div>

            <div className="flex space-x-3">
              {currentStep > 1 && (
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}

              {currentStep < WIZARD_STEPS.length ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Asset Group'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
